import type { ListProgramRolesRequest } from '@compass/client/types/program_role';
import { useQuery } from '@tanstack/react-query';

import { programRoleService } from '@/services/api';
import { useStore } from '@/store/store';

const queryKeys = {
  programRoles: {
    all: ['program-roles'] as const,
    list: (organizationId: string, programId: string, params: any) =>
      [...queryKeys.programRoles.all, organizationId, programId, params] as const,
  },
};

/**
 * Query hook for fetching available roles for a program
 * This hook calls ListProgramRoles endpoint which is program-scoped.
 * Use this to fetch roles that can be assigned to users in a program.
 * Fetches organization and program IDs from the store automatically.
 *
 * @param params - Optional. Additional query parameters (pagination, filters, etc.)
 */
export const useProgramRoles = (params: Partial<ListProgramRolesRequest> = {}) => {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);

  return useQuery({
    queryKey: queryKeys.programRoles.list(organizationId || '', programId || '', params),
    queryFn: () => {
      if (!organizationId || !programId) {
        throw new Error('Organization ID and Program ID are required');
      }
      return programRoleService.listProgramRoles({
        organization_id: organizationId,
        program_id: programId,
        ...params,
      });
    },
    enabled: !!organizationId && !!programId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
